/**
 * SAVA Set Builder
 *
 * Self-contained add-to-cart widget for bikini products that expose a
 * literal "Set/Top/Bottom" option. In Set mode the shopper picks one quick
 * starting size on the page ("Deine Größe"), then "Kaufen" opens a confirm
 * modal where Top size and Bottom size can be adjusted independently before
 * anything is added to the cart - the modal is seeded from the on-page pick
 * (falling back to the nearest available size per part) so the common case
 * needs zero extra taps. Nur Top / Nur Bottom keep a single picker and add
 * straight to the cart, no modal. A confirmed Set add still becomes two
 * grouped cart line items (see the `_sava_set_group` / "Teil von" cart
 * properties below).
 *
 * Deliberately does NOT reuse `<variant-picker>` (section-render + morph,
 * single selection only) - all price/availability logic runs client-side
 * against the product JSON embedded by `snippets/sava-set-builder.liquid`.
 * The confirm modal reuses the theme's own `<dialog-component>`
 * (assets/dialog.js), already registered globally by snippets/scripts.liquid.
 *
 * Loaded as an ES module (unlike sibling sava-*.js scripts) specifically so
 * it can reuse the theme's own `formatMoney` helper and `CartAddEvent` class
 * for perfect parity with a native add-to-cart (same event shape the cart
 * drawer / cart-items-component / cart icon already listen for).
 */
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const SIZE_NAME_RE = /größe|grösse|groesse|size/i;
const COLOR_NAME_RE = /farbe|colour|color/i;
const SET_OPTION_NAME = 'Set/Top/Bottom';
const SIZE_ROLES = ['top', 'bottom', 'single', 'quick'];

class SavaSetBuilder extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;

    const dataEl = this.querySelector('[data-sava-sb-json]');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error('SAVA Set Builder: failed to parse product JSON', err);
      return;
    }

    this._buildIndexes();

    if (this.sizeIdx === -1 || this.setIdx === -1) {
      console.warn('SAVA Set Builder: product is missing a Größe or Set/Top/Bottom option, aborting.');
      return;
    }

    this._initialized = true;

    this._cacheRefs();
    this._resolveModeValues();
    this._bindEvents();
    this._ensureDefaultColor();
    this._ensureDefaultSizes();
    this.update();
  }

  // ---- setup -----------------------------------------------------------

  _buildIndexes() {
    const names = this.data.optionNames || [];
    this.sizeIdx = names.findIndex((name) => SIZE_NAME_RE.test(name));
    this.setIdx = names.indexOf(SET_OPTION_NAME);
    this.colorIdx = names.findIndex((name) => COLOR_NAME_RE.test(name));

    /** @type {Map<string, {id:number, price:number, available:boolean, options:string[]}>} */
    this.variantsByKey = new Map();
    this.anyAvailable = false;

    for (const variant of this.data.variants || []) {
      const key = this._key(
        variant.options[this.sizeIdx],
        variant.options[this.setIdx],
        this.colorIdx >= 0 ? variant.options[this.colorIdx] : null
      );
      this.variantsByKey.set(key, variant);
      if (variant.available) this.anyAvailable = true;
    }
  }

  _key(size, mode, color) {
    return `${size} ${mode} ${this.colorIdx >= 0 ? color : ''}`;
  }

  /** @returns {{id:number, price:number, available:boolean}|null} */
  findVariant(size, mode, color) {
    if (!size || !mode) return null;
    return this.variantsByKey.get(this._key(size, mode, color)) || null;
  }

  _cacheRefs() {
    this.modeInputs = Array.from(this.querySelectorAll('[data-sava-sb-mode-input]'));
    this.colorInputs = Array.from(this.querySelectorAll('[data-sava-sb-color-input]'));

    this.sizeGroups = {};
    this.sizeInputs = {};
    for (const role of SIZE_ROLES) {
      this.sizeGroups[role] = this.querySelector(`[data-sava-sb-size-group="${role}"]`);
      this.sizeInputs[role] = Array.from(
        this.querySelectorAll(`[data-sava-sb-size-input][data-sava-sb-size-role="${role}"]`)
      );
    }

    this.cupEl = this.querySelector('[data-sava-sb-cup]');
    this.priceEl = this.querySelector('[data-sava-sb-price]');
    this.statusEl = this.querySelector('[data-sava-sb-status]');
    this.atcButton = this.querySelector('[data-sava-sb-atc]');
    this.atcLabelEl = this.querySelector('[data-sava-sb-atc-label]');
    this.colorLegendEl = this.querySelector('[data-sava-sb-color-legend]');

    this.modalDialog = this.querySelector('[data-sava-sb-modal]');
    this.modalConfirmBtn = this.querySelector('[data-sava-sb-modal-confirm]');
    this.modalCloseBtn = this.querySelector('[data-sava-sb-modal-close]');
    this.modalStatusEl = this.querySelector('[data-sava-sb-modal-status]');
  }

  _resolveModeValues() {
    const lower = (value) => (value || '').trim().toLowerCase();
    this.modeSet = null;
    this.modeTop = null;
    this.modeBottom = null;

    for (const input of this.modeInputs) {
      const value = lower(input.value);
      if (value === 'set') this.modeSet = input.value;
      else if (value === 'top') this.modeTop = input.value;
      else if (value === 'bottom') this.modeBottom = input.value;
    }
  }

  _bindEvents() {
    this.modeInputs.forEach((input) => input.addEventListener('change', () => this._onModeChange()));
    this.colorInputs.forEach((input) => input.addEventListener('change', () => this.update()));

    for (const role of SIZE_ROLES) {
      this.sizeInputs[role].forEach((input) => input.addEventListener('change', () => this.update()));
    }

    if (this.atcButton) this.atcButton.addEventListener('click', () => this.addToCart());
    if (this.modalConfirmBtn) this.modalConfirmBtn.addEventListener('click', () => this._confirmSetAdd());
    if (this.modalCloseBtn) {
      this.modalCloseBtn.addEventListener('click', () => {
        if (this.modalDialog) this.modalDialog.closeDialog();
      });
    }
  }

  // ---- state helpers -----------------------------------------------------------

  get currentMode() {
    const checked = this.modeInputs.find((input) => input.checked);
    return checked ? checked.value : this.modeSet;
  }

  get currentColor() {
    if (this.colorIdx < 0) return null;
    const checked = this.colorInputs.find((input) => input.checked);
    return checked ? checked.value : null;
  }

  _checkedSize(role) {
    const checked = this.sizeInputs[role].find((input) => input.checked);
    return checked ? checked.value : null;
  }

  _checkSize(role, size) {
    if (!size) return;
    const input = this.sizeInputs[role].find((candidate) => candidate.value === size);
    if (input) input.checked = true;
  }

  _selectDefaultSize(role, mode) {
    const color = this.currentColor;
    const inputs = this.sizeInputs[role];
    if (!inputs.length) return;

    const availableInput = inputs.find((input) => {
      const variant = this.findVariant(input.value, mode, color);
      return variant && variant.available;
    });

    (availableInput || inputs[0]).checked = true;
  }

  _selectDefaultQuickSize() {
    const color = this.currentColor;
    const inputs = this.sizeInputs.quick;
    if (!inputs.length) return;

    // Prefer a size that's available for BOTH parts, so the modal opens
    // with a purchasable Set straight away. Falls back to the first size if
    // no single size covers both (the modal still resolves each part
    // independently when opened).
    const bothAvailable = inputs.find((input) => {
      const top = this.findVariant(input.value, this.modeTop, color);
      const bottom = this.findVariant(input.value, this.modeBottom, color);
      return top && top.available && bottom && bottom.available;
    });

    (bothAvailable || inputs[0]).checked = true;
  }

  /**
   * SAVA: the color radio is preselected in Liquid via `value.selected`, which
   * is simply the product's first variant and never looks at stock. On Last
   * Chance products that is usually a dead color - returns only ever cover a
   * couple of combinations - so the builder would open on a color with no
   * purchasable size at all, and _selectDefaultSize would then fall back to a
   * struck-through size. Move to the first color that actually has stock in
   * the current mode, before sizes get resolved.
   */
  _ensureDefaultColor() {
    if (this.colorIdx < 0 || !this.colorInputs.length) return;

    const mode = this.currentMode;
    const hasStock = (color) =>
      SIZE_ROLES.some((role) =>
        this.sizeInputs[role].some((input) => {
          const variant = this.findVariant(input.value, mode, color);
          return variant && variant.available;
        })
      );

    if (hasStock(this.currentColor)) return;

    const fallback = this.colorInputs.find((input) => hasStock(input.value));
    if (fallback) fallback.checked = true;
  }

  _ensureDefaultSizes() {
    if (!this._checkedSize('top')) this._selectDefaultSize('top', this.modeTop);
    if (!this._checkedSize('bottom')) this._selectDefaultSize('bottom', this.modeBottom);
    if (!this._checkedSize('quick')) this._selectDefaultQuickSize();
  }

  /**
   * Walks the size list outward from `seedSize` (nearest first, larger size
   * preferred on a tie) until it finds one available for `role`/`mode`/
   * `color`. Falls back to the seed itself (or the first size) if nothing is
   * available at all - the caller (the confirm-add path) still guards against
   * actually submitting an unavailable variant.
   */
  _nearestAvailableSize(role, mode, color, seedSize) {
    const inputs = this.sizeInputs[role];
    const order = inputs.map((input) => input.value);
    const isAvailable = (size) => {
      const variant = this.findVariant(size, mode, color);
      return !!(variant && variant.available);
    };

    if (seedSize && isAvailable(seedSize)) return seedSize;

    const seedIdx = order.indexOf(seedSize);
    const start = seedIdx === -1 ? 0 : seedIdx;

    for (let distance = 1; distance < order.length; distance++) {
      const upIdx = start + distance;
      const downIdx = start - distance;
      if (upIdx < order.length && isAvailable(order[upIdx])) return order[upIdx];
      if (downIdx >= 0 && isAvailable(order[downIdx])) return order[downIdx];
    }

    return seedSize || order[0] || null;
  }

  _onModeChange() {
    const mode = this.currentMode;
    const isSingle = mode === this.modeTop || mode === this.modeBottom;

    if (isSingle && !this._checkedSize('single')) {
      // Seed the single-size picker from whichever slot matches, so switching
      // Set -> Nur Top/Nur Bottom keeps a sensible starting point.
      const seedRole = mode === this.modeTop ? 'top' : 'bottom';
      const seedSize = this._checkedSize(seedRole);
      const match = seedSize ? this.sizeInputs.single.find((input) => input.value === seedSize) : null;

      if (match) match.checked = true;
      else this._selectDefaultSize('single', mode);
    }

    this.update();
  }

  // ---- render -----------------------------------------------------------

  update() {
    const mode = this.currentMode;
    const isSet = mode === this.modeSet;
    const color = this.currentColor;

    if (this.sizeGroups.quick) this.sizeGroups.quick.hidden = !isSet;
    if (this.sizeGroups.single) this.sizeGroups.single.hidden = isSet;

    if (this.colorLegendEl && color) this.colorLegendEl.textContent = color;

    this._updateAvailability('top', this.modeTop, color);
    this._updateAvailability('bottom', this.modeBottom, color);
    this._updateAvailability('single', mode, color);

    // The modal's own cup line + confirm state track the modal's Top/Bottom
    // picks. Keep them in sync on every update() (not just while the modal is
    // open) so they're already correct the instant the modal opens.
    this._renderCup(this._checkedSize('top'));
    this._updateModalConfirmState();

    let price = null;
    let canAdd = false;

    if (isSet) {
      const setPricing = this._setPricingForSize(this._checkedSize('quick'), color);
      price = setPricing.price;
      canAdd = setPricing.canAdd;
    } else {
      const variant = this.findVariant(this._checkedSize('single'), mode, color);
      canAdd = !!(variant && variant.available);
      if (variant) price = variant.price;
    }

    this._renderPrice(price);
    this._renderStatus(canAdd);
  }

  /**
   * Set-mode pricing/availability for the page-level "Kaufen" button. Price
   * comes from the exact quick-picked size (Top + Bottom of that size); the
   * "canAdd" gate is intentionally looser - it only asks "is a Set achievable
   * at all for this color", not "is this exact size available for both
   * parts", since opening the confirm modal auto-resolves each part to its
   * nearest available size.
   */
  _setPricingForSize(quickSize, color) {
    const topVariant = this.findVariant(quickSize, this.modeTop, color);
    const bottomVariant = this.findVariant(quickSize, this.modeBottom, color);
    const canAdd = this._hasAvailable('top', this.modeTop, color) && this._hasAvailable('bottom', this.modeBottom, color);

    if (topVariant && bottomVariant) {
      return { price: topVariant.price + bottomVariant.price, canAdd };
    }

    // Quick size doesn't exist for one of the parts (Top/Bottom normally
    // share the same Größe option, so this is a defensive fallback) - use
    // any size that exists for both so the price line still shows something.
    const fallback = this.sizeInputs.top.find((input) => {
      const top = this.findVariant(input.value, this.modeTop, color);
      const bottom = this.findVariant(input.value, this.modeBottom, color);
      return top && bottom;
    });

    if (fallback) {
      const top = this.findVariant(fallback.value, this.modeTop, color);
      const bottom = this.findVariant(fallback.value, this.modeBottom, color);
      if (top && bottom) return { price: top.price + bottom.price, canAdd };
    }

    return { price: null, canAdd };
  }

  _hasAvailable(role, mode, color) {
    return this.sizeInputs[role].some((input) => {
      const variant = this.findVariant(input.value, mode, color);
      return variant && variant.available;
    });
  }

  _updateAvailability(role, mode, color) {
    for (const input of this.sizeInputs[role]) {
      const variant = this.findVariant(input.value, mode, color);
      const available = !!(variant && variant.available);

      input.setAttribute('data-option-available', available ? 'true' : 'false');

      const label = input.closest('.variant-option__button-label');
      const strike = label ? label.querySelector('[data-sava-sb-strike]') : null;
      // The theme forces `display: block` on `.variant-option__strikethrough`,
      // which overrides the `hidden` attribute - so toggle inline display,
      // which wins over the class rule, to actually show/hide the line.
      if (strike) strike.style.display = available ? 'none' : 'block';
    }
  }

  _renderCup(size) {
    if (!this.cupEl) return;

    const cup = size && this.data.cupMap ? this.data.cupMap[size] : null;
    if (!cup) {
      this.cupEl.textContent = '';
      this.cupEl.hidden = true;
      return;
    }

    this.cupEl.textContent = `Größe ${size} entspricht Körbchen ${cup}`;
    this.cupEl.hidden = false;
  }

  _renderPrice(cents) {
    if (!this.priceEl) return;

    if (typeof cents !== 'number') {
      this.priceEl.textContent = '';
      return;
    }

    this.priceEl.textContent = formatMoney(cents, this.data.moneyFormat || '{{ amount }}', this.data.currency || 'EUR');
  }

  _renderStatus(canAdd) {
    if (!this.atcButton) return;

    const isSet = this.currentMode === this.modeSet;
    // SAVA: label stays "In den Warenkorb" in every mode (incl. Set), per user.
    // In Set mode the click still opens the size-confirm modal before adding.
    const readyLabel = 'In den Warenkorb';

    if (!this.anyAvailable) {
      this._setStatus('Dieses Produkt ist aktuell ausverkauft.');
      this.atcButton.disabled = true;
      this._setAtcLabel('Ausverkauft');
      return;
    }

    if (!canAdd) {
      this._setStatus(
        isSet
          ? 'Für dieses Set ist aktuell keine Größe verfügbar.'
          : 'Diese Kombination ist leider ausverkauft.'
      );
      this.atcButton.disabled = true;
      this._setAtcLabel('Ausverkauft');
      return;
    }

    this._setStatus('');
    this.atcButton.disabled = false;
    this._setAtcLabel(readyLabel);
  }

  _setStatus(message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.hidden = !message;
  }

  _setAtcLabel(text) {
    if (this.atcLabelEl) this.atcLabelEl.textContent = text;
  }

  // ---- confirm modal -----------------------------------------------------------

  _seedModalFromQuick() {
    const color = this.currentColor;
    const quickSize = this._checkedSize('quick');

    const topTarget = this._nearestAvailableSize('top', this.modeTop, color, quickSize);
    const bottomTarget = this._nearestAvailableSize('bottom', this.modeBottom, color, quickSize);

    this._checkSize('top', topTarget);
    this._checkSize('bottom', bottomTarget);

    this._updateAvailability('top', this.modeTop, color);
    this._updateAvailability('bottom', this.modeBottom, color);
    this._renderCup(this._checkedSize('top'));
    this._updateModalConfirmState();
  }

  _updateModalConfirmState() {
    if (!this.modalConfirmBtn) return;

    const color = this.currentColor;
    const topVariant = this.findVariant(this._checkedSize('top'), this.modeTop, color);
    const bottomVariant = this.findVariant(this._checkedSize('bottom'), this.modeBottom, color);
    const canConfirm = !!(topVariant && topVariant.available && bottomVariant && bottomVariant.available);

    this.modalConfirmBtn.disabled = !canConfirm;

    if (this.modalStatusEl) {
      if (canConfirm) {
        this.modalStatusEl.textContent = '';
        this.modalStatusEl.hidden = true;
      } else {
        this.modalStatusEl.textContent = 'Diese Größenkombination ist leider nicht auf Lager.';
        this.modalStatusEl.hidden = false;
      }
    }
  }

  _openConfirmModal() {
    if (!this.modalDialog || typeof this.modalDialog.showDialog !== 'function') return;
    this._seedModalFromQuick();
    this.modalDialog.showDialog();
  }

  _confirmSetAdd() {
    if (this._submitting || (this.modalConfirmBtn && this.modalConfirmBtn.disabled)) return;

    const color = this.currentColor;
    const topVariant = this.findVariant(this._checkedSize('top'), this.modeTop, color);
    const bottomVariant = this.findVariant(this._checkedSize('bottom'), this.modeBottom, color);

    if (!topVariant || !bottomVariant || !topVariant.available || !bottomVariant.available) {
      this._updateModalConfirmState();
      return;
    }

    const groupId = `sava_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const partOf = 'Set (Top + Bottom)';

    const items = [
      { id: topVariant.id, quantity: 1, properties: { _sava_set_group: groupId, 'Teil von': partOf } },
      { id: bottomVariant.id, quantity: 1, properties: { _sava_set_group: groupId, 'Teil von': partOf } },
    ];

    if (this.modalDialog && typeof this.modalDialog.closeDialog === 'function') this.modalDialog.closeDialog();
    this._submitItems(items);
  }

  // ---- add to cart -----------------------------------------------------------

  addToCart() {
    if (!this.atcButton || this.atcButton.disabled || this._submitting) return;

    const mode = this.currentMode;
    const isSet = mode === this.modeSet;

    if (isSet) {
      this._openConfirmModal();
      return;
    }

    const color = this.currentColor;
    const variant = this.findVariant(this._checkedSize('single'), mode, color);

    if (!variant || !variant.available) {
      this.update();
      return;
    }

    this._submitItems([{ id: variant.id, quantity: 1 }]);
  }

  _submitItems(items) {
    this._submitting = true;
    this.atcButton.disabled = true;
    this._setAtcLabel('Wird hinzugefügt …');
    this._setStatus('');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const cartAddUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Add to cart failed');
        }

        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

            document.dispatchEvent(
              new CartAddEvent(cart, this.id || 'sava-set-builder', {
                source: 'product-form-component',
                itemCount: totalQuantity,
                productId: String(this.data.productId),
                sections: response.sections,
              })
            );

            this._submitting = false;
            this._flashAdded();
          });
      })
      .catch((err) => {
        console.error('SAVA Set Builder: add to cart failed', err);
        this._submitting = false;
        this._setStatus('Hinzufügen leider fehlgeschlagen. Bitte versuch es erneut.');
        this.update();
      });
  }

  _flashAdded() {
    this._setAtcLabel('Hinzugefügt');
    this.atcButton.disabled = false;

    if (this._flashTimeout) window.clearTimeout(this._flashTimeout);
    this._flashTimeout = window.setTimeout(() => this.update(), 1200);
  }
}

if (!customElements.get('sava-set-builder')) {
  customElements.define('sava-set-builder', SavaSetBuilder);
}
